import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI';
import IncidentProcessedByWmrcTable from './Table/IncidentProcessedByWmrc';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { useSelector } from 'react-redux';
import toasters from '../../../../../../../utils/toasters/Toasters';
import { useSearchParams } from 'react-router-dom'

const formatPayloadDates = (payload) => {
    if (!payload) return payload;

    const formattedPayload = { ...payload };

    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate);
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0];
        }
    }

    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate);
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0];
        }
    }

    return formattedPayload;
};

const IncidentProcessedByWmrc = () => {
    const location = useLocation();
    const { token } = useSelector((state) => state?.auth);
    const queryClient = useQueryClient();
    const [sort, setSort] = useState(null);

    const [searchParams] = useSearchParams();
    const [payload, setPayload] = useState(null);

    useEffect(() => {

    // Try reading from URL params
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const code = searchParams.get('code');

    if (startDate || endDate) {
        const urlPayload = { startDate, endDate, code };
        setPayload(urlPayload);
        sessionStorage.setItem('incidentbyWmrcPayload', JSON.stringify(urlPayload));
        return;
    }

    // fallback to location.state
    if (location.state) {
        setPayload(location.state);
        sessionStorage.setItem('incidentbyWmrcPayload', JSON.stringify(location.state));
        return;
    }

    // fallback to sessionStorage
    const storedPayload = sessionStorage.getItem('incidentbyWmrcPayload');
    if (storedPayload) {
        setPayload(JSON.parse(storedPayload));
    }

}, [location.state, searchParams]);

const formattedPayload = formatPayloadDates(payload);


    const getInitialProcessedByWMRC = useCallback(async () => {
        let apiQueryParams = {};
        if (sort && sort?.columnName && sort.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort?.columnName,
            };
        }
        return fetchIncidentAnalysisData({ ...formattedPayload, ...apiQueryParams, export: 'N' });
    }, [sort, formattedPayload]);

    const handleColumnSort = async (column, sortDirection) => {
        setSort((prev) => ({
            ...prev,
            columnName: column?.sortField,
            order: sortDirection,
        }));
    };

    const {
        data: IncidentProcessedByWmrcData,
        isLoading,
      } = useQuery(
        ['fetch-incident-analysis', formattedPayload, sort],
        getInitialProcessedByWMRC,
        {
          enabled: !!formattedPayload, 
          select: (data) => {
            const totalSentToLocal = data?.reduce((sum, item) => sum + item?.sent_to_local, 0);
            const totalScoreAdjusted = data?.reduce((sum, item) => sum + item?.score_adjusted, 0);
            const total = data?.reduce((sum, item) => sum + item?.total, 0);
      
            const obj = {
              name_tx: 'Total',
              sent_to_local: totalSentToLocal || 0,
              score_adjusted: totalScoreAdjusted || 0,
              total: total || 0,
            };
      
            return [...data, obj];
          },
          retry: (failureCount, error) => {
            const status = error?.response?.status;
            return failureCount < 1 && status >= 400 && status < 600; 
          },
          retryDelay: 1000, 
        }
      );
      
    const tableColumn = ['Full Name', 'Sent To Local', 'Adjusted Score', 'Total'];

    // PDF download function
    const downloadPDF = () => {
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [297, 210],
    });

    doc.setFontSize(18);
    doc.text('Incident Processed By WMRC Report', 14, 22);

    doc.setFontSize(12);
    doc.text(
        `Report Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
        14,
        30
    );

    const tableRows = IncidentProcessedByWmrcData?.map(item => [
        item?.name_tx || '',
        item?.sent_to_local || '',
        item?.score_adjusted || '',
        item?.total || 0
    ]) || [];

    doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 40,
        theme: 'grid',
        // 1. Define margins to align with your text at x=14
        margin: { left: 14, right: 14 },
        // 2. Set width to auto to fill the margins
        tableWidth: 'auto', 
        headStyles: {
            fillColor: [0, 81, 242],
            textColor: [255, 255, 255],
            fontSize: 12,
            halign: 'center' // Better look for full width
        },
        bodyStyles: {
            fontSize: 10,
            cellPadding: 4, // Slightly more padding looks better on wide tables
            halign: 'center'
        },
        alternateRowStyles: {
            fillColor: [245, 245, 245],
        },
        styles: {
            lineColor: [0, 0, 0],
            lineWidth: 0.2, // Thinner lines for a cleaner professional look
            overflow: 'linebreak'
        },
        // 3. Use 'auto' or percentages to divide the space
        columnStyles: {
            0: { cellWidth: 'auto', halign: 'left' }, // Name usually needs more space/left align
            1: { cellWidth: 'auto' },
            2: { cellWidth: 'auto' },
            3: { cellWidth: 'auto' },
        },
    });

    doc.save('incident-processed-by-wmrc-report.pdf');
};

    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' });

        const csvUrl = URL.createObjectURL(csvBlob);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    };

    const ExcelIncidentProcessedByWmrc = async () => {
        try {
            let response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                { ...formattedPayload, export: 'Y' },
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );
            downloadFile(response?.data, 'incident-processed-by-wmrc-report');
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message });
        }
    };

    return (
        <>
            <IncidentProcessedByWmrcTable
                IncidentProcessedByWmrcData={IncidentProcessedByWmrcData}
                downloadPDF={downloadPDF}
                handleColumnSort={handleColumnSort}
                downloadExcel={ExcelIncidentProcessedByWmrc}
                DisplayDate={formattedPayload}
                isLoading={isLoading}
                reportData={formattedPayload}
                reportCode={payload?.code || 'incidentProcessedByWmrc'}
                reportTableData={IncidentProcessedByWmrcData}
                generatePDFFunction={downloadPDF}
            />
        </>
    );
};

export default IncidentProcessedByWmrc;
